import { useCallback, useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigationType } from "react-router-dom";
import { useLocalePath, useT } from "../i18n";
import { useApi } from "../lib/api";
import { EMPTY_FILTERS, activeFilterCount, toApiParams, useInventory, useInventoryQuery } from "../hooks/useInventory";
import { Seo } from "../components/Seo";
import { FilterBar } from "../components/inventory/FilterBar";
import { FilterSheet } from "../components/inventory/FilterSheet";
import { InventoryResults } from "../components/inventory/InventoryResults";
import { FilterChips } from "../components/inventory/FilterChips";

const useScrollMemory = (ready) => {
  const location = useLocation();
  const navType = useNavigationType();
  const restored = useRef(null);
  const storageKey = `inv-scroll:${location.key}`;

  useEffect(() => {
    return () => {
      sessionStorage.setItem(storageKey, String(window.scrollY));
    };
  }, [storageKey]);

  useEffect(() => {
    if (!ready || navType !== "POP" || restored.current === storageKey) return undefined;
    restored.current = storageKey;
    const y = Number(sessionStorage.getItem(storageKey));
    if (!y) return undefined;
    const frame = requestAnimationFrame(() => {
      window.scrollTo(0, y);
    });
    return () => cancelAnimationFrame(frame);
  }, [ready, navType, storageKey]);
};

const ScopeTabs = ({ scope }) => {
  const t = useT();
  const lp = useLocalePath();
  const tab = (id, to, label) => (
    <Link to={lp(to)} aria-current={scope === id ? "page" : undefined} data-testid={`scope-tab-${id}`}
      className={`inline-flex min-h-[44px] items-center border px-5 text-[12px] font-medium uppercase tracking-[0.16em] transition-colors duration-300 ${scope === id ? "border-white bg-white text-ink" : "border-white/25 text-white/70 hover:border-white/60 hover:text-white"}`}>
      {label}
    </Link>
  );
  return (
    <nav aria-label={t("inventory.scopeNav")} className="mt-8 flex" data-testid="scope-tabs">
      {tab("active", "inventory", t("inventory.tabAvailable"))}
      {tab("sold", "inventory/sold", t("inventory.tabSold"))}
    </nav>
  );
};

export default function InventoryPage({ scope = "active" }) {
  const t = useT();
  const lp = useLocalePath();
  const { filters: urlFilters, page, setFilters, setPage } = useInventoryQuery();
  const filters = useMemo(() => ({ ...urlFilters, scope }), [urlFilters, scope]);
  const page_ = scope === "sold" ? "sold" : "inventory";
  const { entry, status, loadingMore, loadMore, retry } = useInventory(filters, page, setPage);
  const { data: facets } = useApi("/vehicles/facets", toApiParams(filters));
  useScrollMemory(status === "ready");

  const update = useCallback((patch) => setFilters({ ...filters, ...patch }), [filters, setFilters]);
  const clear = useCallback(() => setFilters({ ...EMPTY_FILTERS, sort: filters.sort }), [filters.sort, setFilters]);
  const total = entry?.total;
  const noStock = scope === "active" && status === "ready" && total === 0 && activeFilterCount(filters) === 0;

  return (
    <div data-testid="inventory-page" className="bg-paper text-ink">
      <Seo title={t(`pages.${page_}.title`)} description={t(`pages.${page_}.description`)} path={scope === "sold" ? "inventory/sold" : "inventory"} />
      <section className="bg-ink pb-14 pt-32 text-white lg:pb-20 lg:pt-40">
        <div className="container-x">
          <nav aria-label="Breadcrumb" className="text-xs text-white/45" data-testid="breadcrumb">
            <Link to={lp()} className="hover:text-white">{t("nav.home")}</Link>
            <span className="mx-3">/</span>
            <span className="text-white/75">{t("nav.inventory")}</span>
          </nav>
          <div className="mt-10 flex items-end justify-between gap-6">
            <h1 className="h-section font-display" data-testid="page-title">{t(`pages.${page_}.title`)}</h1>
            <p className="micro-label hidden pb-2 tabular-nums text-white/50 sm:block">
              {total > 0 && `${total} ${t("inventory.vehicles")}`}
            </p>
          </div>
          <ScopeTabs scope={scope} />
        </div>
      </section>
      {!noStock && (
        <div className="sticky top-[72px] z-30 border-b border-ink/10 bg-paper/95 py-4 backdrop-blur-md lg:top-20">
          <div className="container-x">
            <FilterBar filters={filters} update={update} setFilters={setFilters} facets={facets} total={total} onClear={clear} activeCount={activeFilterCount(filters)} />
            <FilterSheet filters={filters} setFilters={setFilters} update={update} facets={facets} total={total} />
            <FilterChips filters={filters} update={update} facets={facets} />
          </div>
        </div>
      )}
      <section className="container-x pb-28 pt-12 lg:pb-40 lg:pt-16">
        <InventoryResults entry={entry} status={status} loadingMore={loadingMore} loadMore={loadMore} retry={retry} onClear={clear} noStock={noStock} />
      </section>
    </div>
  );
}
